import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import Button from "components/button"
import Input from "components/input";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import { ISportParticipantItem } from "interfaces";
import { isEmpty } from "lodash";
import { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom"
import { isHaveActionPermission } from "utills/helpers";

interface ParticipantTableProps {
  data?: ISportParticipantItem[];
  pageSize: number;
  currentPage: number;
  _undoCheckParticipantId: (id: string) => void;
  _checkParticipantId:(id: string) => void;
  fetchParticipantIds: () => void;
  participantIds?: string[] | null;
  setParticipantIds: Dispatch<SetStateAction<string[] | null>>;
  _toggleActive: (id: string, isActive: boolean) => void;
}

export const ParticipantTable = ({data = [], fetchParticipantIds, participantIds, _undoCheckParticipantId, _checkParticipantId, setParticipantIds, currentPage, pageSize, _toggleActive }: ParticipantTableProps) => {
  
  return (
    <div className="flow-root">
      <div className="relative overflow-x-auto min-w-full">
        <table className="table-auto w-max divide-y divide-gray-300 bg-magnolia-500 rounded-md min-w-full">
          <thead>
            <tr>
              <th
                scope="col"
                className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
              >
                No
              </th>
              <th scope="col" className="th">
                <Input
                  type="checkbox"
                  disabled={
                    !isHaveActionPermission(
                      "sports",
                      "participants",
                      "export"
                    ) &&
                    !isHaveActionPermission("sports", "participants", "edit")
                  }
                  checked={!isEmpty(participantIds)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      fetchParticipantIds();
                    } else {
                      setParticipantIds([]);
                    }
                  }}
                />
              </th>
              <th scope="col" className="th">
                Participant ID
              </th>
              <th scope="col" className="th">
                Full Name
              </th>
              <th scope="col" className="th">
                Category name
              </th>
              <th scope="col" className="th">
                BIB
              </th>
              <th scope="col" className="th">
                Email
              </th>
              <th scope="col" className="th">
                Phone
              </th>
              <th scope="col" className="th">
                ID card
              </th>
              <th scope="col" className="th">
                Order code
              </th>
              <th scope="col" className="th">
                Event name
              </th>
              <th scope="col" className="th">
                Source
              </th>
              <th scope="col" className="th">
                Status
              </th>
              <th scope="col" className="th">
                Ewaiver status
              </th>
              <th scope="col" className="th">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((participant: ISportParticipantItem, index: number) => (
              <tr key={index}>
                <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                  {(currentPage - 1) * pageSize + 1 + index}
                </td>
                <td className="td">
                  <Input
                    type="checkbox"
                    checked={(participantIds ?? []).includes(
                      participant.participant_id
                    )}
                    disabled={
                      !isHaveActionPermission(
                        "sports",
                        "participants",
                        "export"
                      ) &&
                      !isHaveActionPermission("sports", "participants", "edit")
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        _checkParticipantId(participant.participant_id);
                      } else {
                        _undoCheckParticipantId(participant.participant_id);
                      }
                    }}
                  />
                </td>
                <td className="td">
                <Link to={`/participants/sport/${participant.participant_id}`} className="w-fit text-brand-500 hover:underline">
                  {participant.participant_id}
                </Link>
                </td>
                <td className="td">{participant.full_name}</td>
                <td className="td">{participant.category_name_en}</td>
                <td className="td">{participant.bib}</td>
                <td className="td">{participant.email}</td>
                <td className="td">
                  {participant.phone ? (
                    <p>
                      {participant.phone_area_code ? `(+${participant.phone_area_code})` : ""} {participant.phone}
                    </p>
                  ): <></>}
                </td>
                <td className="td">
                  {participant.id_card}
                </td>
                <td className="td">
                  <Link to={`/orders/${participant.parent_ref_id}`} className="w-fit font-medium text-brand-500 hover:underline">
                    {participant.parent_ref_id}
                  </Link>
                </td>
                <td className="td">
                  {participant.event_name_en}
                </td>
                <td className="td">
                  <Tag text={participant.source} type="blue" customClassName="capitalize" />
                </td>
                <td className="td">
                  <SwitchButton
                    checked={participant.is_active}
                    disabled={!isHaveActionPermission("sports", "participants", "edit")}
                    onChange={() => _toggleActive(participant.participant_id, !participant.is_active)}
                  />
                </td>
                <td className="td">
                  <Tag text={participant.online_checked ? "Signed" : "Unsigned"} type={participant.online_checked ? "green" : "pink"} />
                </td>
                <td className="td flex justify-end items-end gap-x-2 py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                  <Link to={`/participants/sport/${participant.participant_id}`} className="w-fit">
                    <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                      <EyeIcon className="w-4 h-4 text-black" />
                    </Button>
                  </Link>
                  {isHaveActionPermission("sports", "participants", "edit") ? (
                    <Link to={`/participants/sport/${participant.participant_id}/edit`} className="w-fit">
                      <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                        <PencilIcon className="w-4 h-4 text-black" />
                      </Button>
                    </Link>
                  ) : <></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
  </div>
  )
}
